import type { FunctionComponent } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

//useState is no longer destructured for testing
import { Analytics } from '@trello/atlassian-analytics';
import type { NetworkError } from '@trello/graphql-error-handling';
import { Button } from '@trello/nachos/button';
import { RouterLink } from '@trello/router/router-link';
import type { PowerUpTestIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';
import { makeSlug } from '@trello/urls';

import type { BoardOption } from 'app/src/components/BoardSelector';
import { BoardSelector } from 'app/src/components/BoardSelector';
import { Breadcrumbs } from 'app/src/components/Breadcrumbs';
import { getMarketingScreenInfo } from 'app/src/getMarketingScreenInfo';
import { BoardIcon } from './BoardIcon';
import { DataConsent } from './DataConsent';
import { useEnablePluginMutation } from './EnablePluginMutation.generated';
import type { Plugin } from './types';

import styles from './EnablePage.less';

// eslint-disable-next-line @trello/assets-alongside-implementation
import addPowerUpIcon from 'resources/images/directory/icons/add-pup.svg';

export type EnablePageStatus = 'failed' | 'in_progress' | 'pending' | 'success';

const getPluginTrackingName = (plugin: Plugin) => {
  // We only want to include public power-up names in
  // GAS events to avoid UGC violations for private power-ups
  const pluginName = plugin.listing ? plugin.listing?.name : '';
  return plugin.public ? pluginName : undefined;
};

export const Header: FunctionComponent<{
  plugin: Plugin;
  board?: BoardOption;
}> = ({ plugin, board }) => {
  const intl = useIntl();
  const pluginName = plugin.listing ? plugin.listing.name : '';
  const pluginIcon = plugin.icon.url;

  return (
    <div>
      <Breadcrumbs
        items={[
          {
            title: intl.formatMessage({
              id: 'templates.directory.power-ups',
              defaultMessage: 'Power-Ups',
              description: 'Title for the Power-Ups list',
            }),
            href: '/power-ups',
          },
          {
            title: pluginName,
            href: `/power-ups/${plugin!.id}/${makeSlug(pluginName)}`,
          },
        ]}
      />
      <div className={styles.flexAndCenter}>
        <div className={styles.enableIconContainer}>
          <div className={styles.flexAndCenter}>
            <div
              className={styles.largePowerUpIcon}
              style={{ backgroundImage: `url('${pluginIcon}')` }}
              data-testid={getTestId<PowerUpTestIds>('pup-large-icon')}
            />
          </div>
          <div className={styles.enableIconSubtitle}>{pluginName}</div>
        </div>
        <div className={styles.addPowerUpIcon}>
          <img alt="" role="presentation" src={addPowerUpIcon} />
        </div>
        <div className={styles.enableIconContainer}>
          <div className={styles.flexAndCenter}>
            <BoardIcon boardOption={board} />
          </div>
          <div className={styles.enableIconSubtitle}>Trello</div>
        </div>
      </div>
    </div>
  );
};

export const PendingState: FunctionComponent<{
  plugin: Plugin;
  boardOption?: BoardOption;
  enableThisPlugin: () => void;
  isEnabling: boolean;
}> = ({ plugin, enableThisPlugin, isEnabling, boardOption }) => {
  const handleCancelEnablePlugin = () => {
    Analytics.sendClickedLinkEvent({
      linkName: 'cancelButton',
      attributes: {
        powerUpId: plugin.id,
        powerUpName: getPluginTrackingName(plugin),
        isPowerUpPublic: plugin.public,
        idOrganizationOwner: plugin.idOrganizationOwner,
      },
      source: 'publicPowerUpAddToBoardScreen',
    });
  };

  return (
    <div
      data-testid={getTestId<PowerUpTestIds>('pup-enable-page-pending-state')}
    >
      <DataConsent plugin={plugin} />

      <div className={styles.enableButtonsContainer}>
        <RouterLink
          href={`/power-ups/${plugin.id}`}
          onClick={handleCancelEnablePlugin}
        >
          <FormattedMessage
            id="templates.directory_power_up_item.cancel"
            defaultMessage="Cancel"
            description="Cancel"
          />
        </RouterLink>
        <Button
          appearance="primary"
          className={styles.enableButton}
          isDisabled={!boardOption || isEnabling}
          onClick={enableThisPlugin}
          data-testid={getTestId<PowerUpTestIds>('pup-add-to-board-button')}
        >
          <FormattedMessage
            id="templates.directory_power_up_item.add"
            defaultMessage="Add"
            description="Add"
          />
        </Button>
      </div>
    </div>
  );
};

export const SuccessState: FunctionComponent<{
  plugin: Plugin;
  board: BoardOption;
}> = ({ plugin, board }) => {
  return (
    <div className={styles.successContainer}>
      <h1
        className={styles.successHeading}
        data-testid={getTestId<PowerUpTestIds>('pup-success-heading')}
      >
        🎉
        <FormattedMessage
          id="templates.directory.enabled-the-power-up"
          defaultMessage="We’ve enabled the Power-Up!"
          description="Power-Up enabled"
        />
      </h1>

      <div className={styles.successText}>
        <FormattedMessage
          id="templates.directory.the-power-up-has-been-added-to-your-board"
          defaultMessage="The {pluginName} Power-Up has been added to {boardName}."
          description="The power-up has been added to your board"
          values={{
            boardName: board.value.name || '',
            pluginName: plugin.listing ? plugin.listing.name : '',
          }}
        />
      </div>

      <div className={styles.successButtons}>
        <RouterLink href={`/power-ups/${plugin.id}`}>
          <FormattedMessage
            id="templates.directory.back-to-listing"
            defaultMessage="Back to listing"
            description="Back to listing"
          />
        </RouterLink>
        <RouterLink href={`/b/${board.value.id}`} className={styles.buttonLink}>
          <Button
            appearance="primary"
            data-testid={getTestId<PowerUpTestIds>('pup-go-to-board-button')}
          >
            <FormattedMessage
              id="templates.directory.go-to-board"
              defaultMessage="Go to board"
              description="Go to board"
            />
          </Button>
        </RouterLink>
      </div>
    </div>
  );
};

export const ErrorState: FunctionComponent<{
  plugin: Plugin;
  board: BoardOption;
  error: { message: string; networkError?: NetworkError };
  resetPageState: () => void;
}> = ({ plugin, board, error, resetPageState }) => {
  const pluginName = plugin.listing ? plugin.listing.name : '';
  const boardName = board.value.name;
  const actualError = error.networkError?.code || error.message;

  switch (actualError) {
    case 'PLUGIN_ALREADY_ENABLED':
      return (
        <div className={styles.errorContainer}>
          <div className={styles.flexAndCenter}>
            <div className={styles.errorAlert}>
              <FormattedMessage
                id="templates.directory.the-power-up-has-already-been-added-to-the-board"
                defaultMessage="The Power-Up has already been added to the board."
                description="The power-up has already been added to the board"
              />
            </div>
          </div>

          <div className={styles.successButtons}>
            <RouterLink href={`/power-ups/${plugin.id}`}>
              <FormattedMessage
                id="templates.directory.back-to-listing"
                defaultMessage="Back to listing"
                description="Back to listing"
              />
            </RouterLink>
            <RouterLink
              className={styles.buttonLink}
              href={`/b/${board.value.id}`}
            >
              <Button appearance="primary">
                <FormattedMessage
                  id="templates.directory.go-to-board"
                  defaultMessage="Go to board"
                  description="Go to board"
                />
              </Button>
            </RouterLink>
          </div>
        </div>
      );
    case 'PLUGIN_NOT_ALLOWED':
      return (
        <div className={styles.errorContainer}>
          <div className={styles.flexAndCenter}>
            <div className={styles.errorAlert}>
              <FormattedMessage
                id="templates.directory.this-plugin-is-not-allowed-by-this-boards-enterprise"
                defaultMessage="This plugin is not allowed by this board's enterprise."
                description="Error message when plugin is not allowed by board’s enterprise"
              />
            </div>
          </div>

          <div className={styles.errorButtons}>
            <RouterLink href={`/power-ups/${plugin.id}`}>
              <FormattedMessage
                id="templates.directory.back-to-listing"
                defaultMessage="Back to listing"
                description="Back to listing"
              />
            </RouterLink>
          </div>
        </div>
      );
    default:
      return (
        <div className={styles.errorContainer}>
          <h1 className={styles.errorHeading}>
            <FormattedMessage
              id="templates.directory.hmmm-something-went-wrong"
              defaultMessage="Hmmm… something went wrong…"
              description="Error message when something goes wrong"
            />
          </h1>

          <div className={styles.errorText}>
            <FormattedMessage
              id="templates.directory.the-power-up-has-not-been-added-the-board"
              defaultMessage="The {pluginName} Power-Up has not been added to {boardName}."
              description="The power-up has not been added to the board"
              values={{
                boardName,
                pluginName,
              }}
            />
          </div>

          <div className={styles.errorButtons}>
            <RouterLink href={`/power-ups/${plugin.id}`}>
              <FormattedMessage
                id="templates.directory.back-to-listing"
                defaultMessage="Back to listing"
                description="Back to listing"
              />
            </RouterLink>

            <Button appearance="primary" onClick={() => resetPageState()}>
              <FormattedMessage
                id="templates.directory.try-again"
                defaultMessage="Try again"
                description="Try again"
              />
            </Button>
          </div>
        </div>
      );
  }
};

export const EnablePage: FunctionComponent<{
  plugin: Plugin;
}> = ({ plugin }) => {
  const [selectedBoard, setSelectedBoard] = useState<BoardOption | undefined>(
    undefined,
  );
  //useState is not destructured for testing
  const [pageState, setPageState] = useState<EnablePageStatus>('pending');
  const [pageError, setPageError] = useState<NetworkError | undefined>(
    undefined,
  );
  const [enablePlugin, { loading }] = useEnablePluginMutation();

  useEffect(() => {
    const { url, referrerUrl, referrerScreenName } = getMarketingScreenInfo();
    Analytics.sendMarketingScreenEvent({
      url,
      referrerUrl,
      referrerScreenName,
      event: {
        source: 'publicPowerUpAddToBoardScreen',
        name: 'publicPowerUpAddToBoardScreenV2',
        attributes: {
          powerUpId: plugin.id,
          powerUpName: getPluginTrackingName(plugin),
          isPowerUpPublic: plugin.public,
          idOrganizationOwner: plugin.idOrganizationOwner,
          pluginTags: plugin.tags,
        },
      },
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const enableThisPlugin = useCallback(async () => {
    if (!selectedBoard) {
      throw new Error('Board not selected.');
    }

    const pluginAttributes = {
      pluginId: plugin.id,
      pluginName: getPluginTrackingName(plugin),
      pluginTags: plugin.tags,
      installSource: 'publicDirectory',
    };
    const traceId = Analytics.startTask({
      taskName: 'enable-plugin',
      source: 'publicPowerUpAddToBoardScreen',
    });
    try {
      const pluginId = plugin.id;
      const boardId = selectedBoard.value.id;

      await enablePlugin({
        variables: { pluginId, boardId, traceId },
      });

      // plugin wrapper from plugin-behavioral-analytics doesn't work here
      // so we're "faking" it here instead until we find a way
      Analytics.sendTrackEvent({
        action: 'added',
        actionSubject: 'powerUp',
        attributes: {
          powerUpId: pluginId,
          powerUpName: getPluginTrackingName(plugin),
          isPowerUpPublic: plugin.public,
          idOrganizationOwner: plugin.idOrganizationOwner,
          installSource: 'publicDirectory',
          tags: plugin.tags,
          taskId: traceId,
        },
        containers: {
          board: { id: boardId },
          organization: { id: selectedBoard.value?.idOrganization },
        },
        source: 'addPowerUpButton',
      });
      Analytics.taskSucceeded({
        taskName: 'enable-plugin',
        source: 'publicPowerUpAddToBoardScreen',
        attributes: pluginAttributes,
        traceId,
      });
      setPageState('success');
    } catch (e) {
      Analytics.taskFailed({
        taskName: 'enable-plugin',
        source: 'publicPowerUpAddToBoardScreen',
        attributes: pluginAttributes,
        traceId,
        error: e,
      });
      setPageState('failed');
      // @ts-expect-error
      setPageError(e);
    }
  }, [enablePlugin, plugin, selectedBoard]);

  let content;

  switch (pageState) {
    case 'success':
      content = <SuccessState plugin={plugin} board={selectedBoard!} />;
      break;
    case 'failed':
      content = (
        <ErrorState
          plugin={plugin}
          board={selectedBoard!}
          error={pageError!}
          resetPageState={() => {
            setPageError(undefined);
            setPageState('pending');
          }}
        />
      );
      break;
    default:
      content = (
        <PendingState
          plugin={plugin}
          boardOption={selectedBoard}
          isEnabling={loading}
          enableThisPlugin={enableThisPlugin}
        />
      );
  }

  return (
    <div className={styles.listingWrapper}>
      <Header plugin={plugin} board={selectedBoard} />
      <div className={styles.flexAndCenter}>
        <div
          className={styles.enableBoardSelector}
          data-testid={getTestId<PowerUpTestIds>('pup-board-selector')}
        >
          <BoardSelector
            onChange={(boardOption) => {
              setPageError(undefined);
              setPageState('pending');
              setSelectedBoard(boardOption);
            }}
            isDisabled={loading || pageState !== 'pending'}
          />
        </div>
      </div>
      {content}
    </div>
  );
};
